import { type MouseEvent, useCallback, useEffect, useRef, useState } from "react";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useSlider = (
    wrapperRef: React.MutableRefObject<HTMLDivElement | null>,
    anyItemRef: React.MutableRefObject<HTMLDivElement | null>,
    onScrolled?: () => void,
    onBrowse?: () => void,
    amount?: number,
) => {
    const isRTL = typeof window !== "undefined" ? document.documentElement.dir === "rtl" : false;
    const scrollLeft = useRef<number>(0);
    const startX = useRef<number>(0);
    const [prevButtonDisabled, setPrevButtonDisabled] = useState<boolean>(false);
    const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const trackScroll = useRef<boolean>(false);

    const [currentSlide, setCurrentSlide] = useState<number>(0);

    const [scrollable, setScrollable] = useState<boolean>(false);

    // Update scrollable state whenever wrapper dimensions change
    const updateScrollable = useCallback(() => {
        if (wrapperRef.current) {
            const isScrollable = wrapperRef.current.scrollWidth > wrapperRef.current.offsetWidth;
            setScrollable(isScrollable);
        }
    }, [wrapperRef]);

    useEffect(() => {
        const rafId = requestAnimationFrame(() => {
            try {
                updateScrollable();
            } catch (error) {
                console.error("Error updating scrollable:", error);
            }
        });
        return () => cancelAnimationFrame(rafId);
    }, [amount, updateScrollable]);

    useEffect(() => {
        if (wrapperRef.current) {
            if (typeof wrapperRef.current.scrollTo === "function") {
                wrapperRef.current.scrollTo({
                    left: 0,
                    behavior: "instant",
                });
            } else {
                wrapperRef.current.scrollLeft = 0;
            }

            setCurrentSlide(0);
        }
    }, [amount, wrapperRef]);

    const navigate = useCallback(
        (direction: 1 | -1 = 1) =>
            () => {
                onBrowse?.();
                if (wrapperRef.current) {
                    const anyItemWidth = anyItemRef.current?.clientWidth || 0;
                    wrapperRef.current.scrollTo({
                        left: wrapperRef.current.scrollLeft + anyItemWidth * direction,
                        behavior: "smooth",
                    });
                }
            },
        [wrapperRef, anyItemRef, onBrowse],
    );

    const onMouseDown = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (wrapperRef.current) {
                setIsDragging(true);

                startX.current = event.pageX - wrapperRef.current.offsetLeft;
                scrollLeft.current = wrapperRef.current.scrollLeft;
            }
        },
        [wrapperRef],
    );

    const onMouseLeave = useCallback(() => {
        if (wrapperRef.current) {
            setIsDragging(false);
        }
    }, [wrapperRef]);

    const onMouseUp = useCallback(() => {
        if (wrapperRef.current) {
            setIsDragging(false);
        }
    }, [wrapperRef]);

    const onMouseMove = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (wrapperRef.current) {
                if (!isDragging) return;
                const x = event.pageX - wrapperRef.current.offsetLeft;
                const walk = (x - startX.current) * 2; //scroll-fast
                wrapperRef.current.scrollLeft = scrollLeft.current - walk;
            }
        },
        [wrapperRef, isDragging],
    );

    const setButtonStates = useCallback(() => {
        if (wrapperRef.current) {
            const target = wrapperRef.current;
            // For RTL, the scrollLeft is negative
            if (isRTL) {
                setPrevButtonDisabled(target.scrollWidth - target.offsetWidth - Math.abs(target.scrollLeft) <= 1);
                setNextButtonDisabled(target.scrollLeft === 0);
            } else {
                setPrevButtonDisabled(target.scrollLeft === 0);
                setNextButtonDisabled(target.scrollWidth - target.offsetWidth - target.scrollLeft <= 1);
            }

            updateScrollable();
        }
    }, [wrapperRef]);

    const initialPointerPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        trackScroll.current = true;
        initialPointerPosition.current = { x: event.clientX, y: event.clientY };
    }, []);

    const onPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (trackScroll.current) {
                const dx = event.clientX - initialPointerPosition.current.x;
                const dy = event.clientY - initialPointerPosition.current.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance > 10) {
                    onScrolled?.();
                    trackScroll.current = false;
                }
            }
        },
        [trackScroll.current, onScrolled],
    );

    const onPointerLeave = useCallback(() => {
        trackScroll.current = false;
    }, []);

    const onPointerUp = useCallback(() => {
        trackScroll.current = false;
    }, []);

    const onScroll = useCallback(() => {
        setButtonStates();

        if (wrapperRef.current && anyItemRef.current) {
            setCurrentSlide(Math.round(wrapperRef.current.scrollLeft / anyItemRef.current?.clientWidth));
        }
    }, [setButtonStates, wrapperRef, anyItemRef]);

    useEffect(() => {
        setButtonStates();
    }, [setButtonStates]);

    return {
        navigate,
        prevButtonDisabled,
        nextButtonDisabled,
        isDragging,
        currentSlide,
        elementProps: {
            onMouseDown,
            onMouseLeave,
            onMouseUp,
            onMouseMove,
            onPointerDown,
            onPointerMove,
            onPointerLeave,
            onPointerUp,
            onScroll,
        },
        scrollable,
    };
};
